import type { Product } from '@virginexperiencedays/products';

export type ProductChoice = {
  slug: string;
  title: string;
  mainImageUrl: string;
  mainImageAltText: string;
  category?: { title: string; slug: string };
};

export type CollectionProductChoices = {
  collectionTitle: string;
  choices: ProductChoice[];
};

type CollectionChoicesData = {
  isCollectionProduct: boolean;
  collectionChoices: CollectionProductChoices[];
  choicesCount: number;
};

export const collectionChoicesExtractor = (product?: Product): CollectionChoicesData => {
  if (!product) {
    return {
      isCollectionProduct: false,
      collectionChoices: [],
      choicesCount: 0,
    };
  }

  const isCollectionProduct = product?.isCollectionProduct;
  const choicesCount = product?.productChoices?.length ?? 0;

  const sortedCollections: CollectionProductChoices[] = categoriseCollectionChoices(
    product?.productChoices
  );

  return {
    isCollectionProduct,
    collectionChoices: sortedCollections,
    choicesCount,
  };
};

/**
 * Sorts and groups product choices by their category.
 *
 * @param {ProductChoice[]} productChoices - An array of product choices to be sorted and grouped.
 * @returns {CollectionProductChoices[]} An array of collection product choices, each containing a collection title and the associated choices.
 */
function categoriseCollectionChoices(productChoices: ProductChoice[]): CollectionProductChoices[] {
  const categoriesMap = new Map<string, ProductChoice['category']>();

  productChoices.forEach((item) => {
    if (item?.category?.slug) {
      categoriesMap.set(item.category.slug, item.category);
    }
  });

  return Array.from(categoriesMap.values()).map((category) => ({
    collectionTitle: category.title,
    choices: productChoices.filter((choice) => choice?.category?.slug === category.slug),
  }));
}
